import { useCallback, useMemo } from 'react';
import { useAuth } from '@/features/auth/AuthContext';
import { useUiStore } from '@/stores/ui.store';
import { can, canAny, canAll, canGlobal } from '@/lib/rbac';

/**
 * UI gating — enforcement layer 5 (docs/03 §4). Convenience only: the API re-checks
 * every permission, so a bug here is a cosmetic bug, never a security hole.
 *
 * The check defaults to the **active section**, which is the behaviour a section-scoped
 * screen wants: holding `invoice.approve` in Agency must not light up an approve button
 * while the user is looking at Real Estate. Pass `null` explicitly to ask the looser
 * "in any section I hold" question that a group-wide screen needs.
 *
 * @returns {(permission:string, sectionId?:string|null) => boolean}
 */
export const usePermission = () => {
  const { access } = useAuth();
  const activeSectionId = useUiStore((state) => state.activeSectionId);

  return useCallback(
    (permission, sectionId = activeSectionId) => can(access, permission, sectionId),
    [access, activeSectionId],
  );
};

export const usePermissionAny = () => {
  const { access } = useAuth();
  const activeSectionId = useUiStore((state) => state.activeSectionId);

  return useCallback(
    (permissions, sectionId = activeSectionId) => canAny(access, permissions, sectionId),
    [access, activeSectionId],
  );
};

export const usePermissionAll = () => {
  const { access } = useAuth();
  const activeSectionId = useUiStore((state) => state.activeSectionId);

  return useCallback(
    (permissions, sectionId = activeSectionId) => canAll(access, permissions, sectionId),
    [access, activeSectionId],
  );
};

/**
 * The group-level check, for screens that show every section's numbers at once.
 *
 * Kept separate from `usePermission` on purpose: `can(p, null)` answers "anywhere?",
 * which would let a single-section grant open a group-wide screen. See `canGlobal`.
 */
export const useGlobalPermission = () => {
  const { access } = useAuth();
  return useCallback((permission) => canGlobal(access, permission), [access]);
};

/**
 * Both checkers together — what the navigation filter and the shell need.
 * Memoised so callers can use the object as a `useMemo`/`useEffect` dependency.
 */
export const usePermissionCheckers = () => {
  const can = usePermission();
  const canGlobalCheck = useGlobalPermission();

  return useMemo(() => ({ can, canGlobal: canGlobalCheck }), [can, canGlobalCheck]);
};

export default usePermission;
